import React, { useState, useEffect, useRef } from 'react';
import { Stage, LocalStageStream, SubscribeType, StageEvents } from 'amazon-ivs-web-broadcast';
import { streamStatusPoller } from '../services/streamStatusPoller';
import { ivsService } from '../services/ivsService';
import { useBroadcast } from '../contexts/BroadcastContext';
import { logger } from '../utils/logger';
import './BrowserBroadcaster.css';

interface BrowserBroadcasterProps {
  streamId: string;
  streamName: string;
  onStatusChange?: (status: 'idle' | 'connecting' | 'live' | 'stopped' | 'error') => void;
}

const BrowserBroadcaster: React.FC<BrowserBroadcasterProps> = ({ streamId, streamName, onStatusChange }) => {
  const { startBroadcast, stopBroadcast: stopGlobalBroadcast } = useBroadcast();
  const [status, setStatus] = useState<'idle' | 'connecting' | 'live' | 'stopped' | 'error'>('idle');
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [reconnectAttempts, setReconnectAttempts] = useState<number>(0);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const stageRef = useRef<Stage | null>(null);
  const localAudioStreamRef = useRef<LocalStageStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const testStreamRef = useRef<MediaStream | null>(null);
  const stopBroadcastingRef = useRef<(() => void) | null>(null);
  const durationIntervalRef = useRef<number | null>(null);

  // Load available audio devices
  useEffect(() => {
    loadAudioDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update status callback
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  // Store stopBroadcasting reference for global poller callback
  useEffect(() => {
    stopBroadcastingRef.current = stopBroadcasting;
  });

  // Manage global status polling based on broadcast state
  useEffect(() => {
    if (status === 'live') {
      logger.info(`🔍 Starting global status poller for stream: ${streamId}`);

      // Start the global poller
      streamStatusPoller.startPolling(streamId, (backendStatus) => {
        logger.info(`🔍 Global poller callback triggered, backend status: ${backendStatus}`);

        // Call stopBroadcasting when backend reports stopped
        if (stopBroadcastingRef.current) {
          stopBroadcastingRef.current();
        }
      });
    }
    // Note: No cleanup, no stopping on other status changes
    // The poller will only be stopped explicitly by stopBroadcasting()
  }, [status, streamId]);

  // Cleanup on component unmount
  // Cleanup on unmount - but ONLY if we're not broadcasting via context
  // The context will handle cleanup when explicitly stopping
  useEffect(() => {
    return () => {
      logger.info('🧹 BrowserBroadcaster unmounting...');

      // If the broadcast is active in the global context, DON'T clean up
      // The PersistentBroadcaster and context will handle it
      // Only clean up local UI state (audio monitoring, test streams, intervals)

      // Stop audio monitoring (just UI feedback)
      stopAudioLevelMonitoring();

      // Stop test stream if it exists (not the live stream)
      if (testStreamRef.current) {
        testStreamRef.current.getTracks().forEach(track => track.stop());
        testStreamRef.current = null;
      }

      // Clear intervals (just UI state)
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      // NOTE: We do NOT stop the live media stream, leave the stage, or stop polling here
      // because those need to persist when the modal closes but broadcast continues.
      // The stopBroadcast function in context will handle all of that when explicitly stopping.
    };
  }, []);

  const loadAudioDevices = async () => {
    try {
      logger.info('🎤 Requesting microphone permission...');

      // Request permissions first
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      logger.info('✅ Microphone permission granted');
      stream.getTracks().forEach(track => track.stop()); // Stop immediately

      // Now enumerate devices
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices.filter(device => device.kind === 'audioinput');

      logger.info(`📱 Found ${audioInputs.length} audio input device(s)`, {
        devices: audioInputs.map(d => d.label || d.deviceId)
      });

      setDevices(audioInputs);
      if (audioInputs.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(audioInputs[0].deviceId);
        logger.info(`🎯 Auto-selected device: ${audioInputs[0].label || audioInputs[0].deviceId}`);
      }

      // Clear any previous error
      setError(null);
    } catch (err: any) {
      logger.error('❌ Error loading audio devices', err);
      const errorMessage = err.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow microphone access in your browser settings and refresh the page.'
        : `Unable to access microphone: ${err.message}`;
      setError(errorMessage);
    }
  };

  const startAudioLevelMonitoring = (stream: MediaStream) => {
    logger.info('📊 Starting audio level monitoring...');

    // Create audio context and analyser
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    // Configure analyser for better sensitivity
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);

    logger.info('✅ Audio context created:', {
      sampleRate: audioContext.sampleRate,
      state: audioContext.state,
      analyserFFTSize: analyser.fftSize,
      frequencyBinCount: analyser.frequencyBinCount
    });

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    // Start monitoring loop
    const dataArray = new Uint8Array(analyser.fftSize);
    let frameCount = 0;

    const monitorLevel = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteTimeDomainData(dataArray);

      // Calculate RMS level
      let sumSquares = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }
      const rms = Math.sqrt(sumSquares / dataArray.length);
      const level = Math.min(100, rms * 100 * 3);

      setAudioLevel(level);

      // Log every 60 frames
      frameCount++;
      if (frameCount % 60 === 0) {
        logger.info(`🎚️ Audio level: ${level.toFixed(2)}`);
      }

      animationFrameRef.current = requestAnimationFrame(monitorLevel);
    };

    monitorLevel();
    logger.info('✅ Audio monitoring loop started');
  };

  const stopAudioLevelMonitoring = () => {
    logger.info('🛑 Stopping audio level monitoring...');

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setAudioLevel(0);
    logger.info('✅ Audio monitoring stopped');
  };

  const startTestAudio = async () => {
    try {
      logger.info('🧪 Starting audio test...');

      const constraints: MediaStreamConstraints = {
        audio: selectedDeviceId
          ? { deviceId: { exact: selectedDeviceId } }
          : true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const audioTracks = stream.getAudioTracks();
      logger.info(`✅ Test audio stream obtained: ${audioTracks.length} tracks`);

      testStreamRef.current = stream;
      setIsTesting(true);

      // Start audio level monitoring
      startAudioLevelMonitoring(stream);
    } catch (err: any) {
      logger.error('Error starting audio test', err);
      setError(err.message || 'Failed to start audio test');
    }
  };

  const stopTestAudio = () => {
    logger.info('🛑 Stopping audio test...');

    if (testStreamRef.current) {
      testStreamRef.current.getTracks().forEach(track => track.stop());
      testStreamRef.current = null;
    }

    stopAudioLevelMonitoring();
    setIsTesting(false);
    logger.info('✅ Audio test stopped');
  };

  const attemptReconnect = async () => {
    const MAX_RECONNECT_ATTEMPTS = 3;
    const RECONNECT_DELAY = 2000; // 2 seconds

    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      logger.error('❌ Max reconnection attempts reached');
      setError(`Unable to reconnect after ${MAX_RECONNECT_ATTEMPTS} attempts. Please try starting broadcast again.`);
      setStatus('error');
      setIsReconnecting(false);
      setReconnectAttempts(0);
      return;
    }

    setIsReconnecting(true);
    setReconnectAttempts(prev => prev + 1);
    logger.info(`🔄 Attempting to reconnect (${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})...`);

    // Wait before reconnecting
    await new Promise(resolve => setTimeout(resolve, RECONNECT_DELAY));

    try {
      // Clean up existing stage connection
      if (stageRef.current) {
        stageRef.current.leave();
        stageRef.current = null;
      }

      // Restart broadcasting with existing stream
      if (mediaStreamRef.current) {
        logger.info('🔄 Reconnecting with existing media stream...');
        setStatus('connecting');

        // Get fresh IVS token
        const tokenResponse = await ivsService.createParticipantToken(streamId, 'PUBLISH');
        logger.info('✅ New IVS token received for reconnection');

        // Create new LocalStageStream
        const audioTrack = mediaStreamRef.current.getAudioTracks()[0];
        const localAudioStream = new LocalStageStream(audioTrack);
        localAudioStreamRef.current = localAudioStream;

        // Create strategy
        const strategy = {
          stageStreamsToPublish() {
            return [localAudioStream];
          },
          shouldPublishParticipant() {
            return true;
          },
          shouldSubscribeToParticipant() {
            return SubscribeType.NONE;
          }
        };

        // Create new stage with event listeners
        const stage = new Stage(tokenResponse.token, strategy);
        setupStageEventListeners(stage);
        stageRef.current = stage;

        // Join stage
        await stage.join();
        logger.info('✅ Reconnection successful!');

        setStatus('live');
        setIsReconnecting(false);
        setReconnectAttempts(0);
        setError(null);
      }
    } catch (err: any) {
      logger.error('❌ Reconnection failed', err);
      // Try again
      await attemptReconnect();
    }
  };

  const setupStageEventListeners = (stage: Stage) => {
    // Connection state changes
    stage.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (state: string) => {
      logger.info(`🔄 Stage connection state: ${state}`);

      if (state === 'disconnected' && status === 'live' && !isReconnecting) {
        logger.warn('⚠️ Stage disconnected unexpectedly - attempting reconnect...');
        attemptReconnect();
      }
    });

    // Participant events
    stage.on(StageEvents.STAGE_PARTICIPANT_JOINED, (participant: any) => {
      logger.info(`👋 Participant joined: ${participant.id}`);
    });

    stage.on(StageEvents.STAGE_PARTICIPANT_LEFT, (participant: any) => {
      logger.info(`👋 Participant left: ${participant.id}`);
    });

    // Stream events
    stage.on(StageEvents.STAGE_STREAM_MUTE_CHANGED, (participant: any, stream: any) => {
      logger.info('🔇 Stream mute changed', {
        participant: participant.id,
        muted: stream.isMuted
      });
    });

    // Error handling
    stage.on(StageEvents.STAGE_PARTICIPANT_PUBLISH_STATE_CHANGED, (participant: any, state: string) => {
      logger.info('📡 Publish state changed', {
        participant: participant.id,
        state
      });

      if (state === 'errored') {
        logger.error('❌ Publish error for participant', null, {
          participantId: participant.id
        });
        setError('Publishing stream failed. Attempting to reconnect...');
        attemptReconnect();
      }
    });
  };

  const startBroadcasting = async () => {
    try {
      logger.info('🚀 Starting IVS Stage broadcast...');

      // Stop test audio if running
      if (isTesting) {
        logger.info('  Stopping test audio before broadcasting...');
        stopTestAudio();
      }

      setStatus('connecting');
      setError(null);

      // Get media stream from selected device
      const constraints: MediaStreamConstraints = {
        audio: selectedDeviceId
          ? { deviceId: { exact: selectedDeviceId } }
          : true
      };

      logger.info('🎤 Requesting media stream with constraints', { constraints });
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const audioTracks = stream.getAudioTracks();
      logger.info('✅ Media stream obtained:', {
        trackCount: audioTracks.length,
        tracks: audioTracks.map(t => ({
          label: t.label,
          enabled: t.enabled,
          readyState: t.readyState
        }))
      });
      mediaStreamRef.current = stream;

      // Add track ended listener for error recovery
      audioTracks.forEach(track => {
        track.addEventListener('ended', () => {
          logger.error('❌ Audio track ended unexpectedly');
          setError('Audio device disconnected. Please reconnect your device and restart broadcast.');
          setStatus('error');
          stopBroadcasting();
        });
      });

      // Start audio level monitoring
      startAudioLevelMonitoring(stream);

      // Get IVS participant token from backend
      logger.info('🔑 Requesting IVS participant token (PUBLISH)...');
      const tokenResponse = await ivsService.createParticipantToken(streamId, 'PUBLISH');
      logger.info('✅ IVS token received:', {
        participantId: tokenResponse.participantId,
        expiresAt: tokenResponse.expiresAt
      });

      // Create LocalStageStream from audio track
      const audioTrack = audioTracks[0];
      const localAudioStream = new LocalStageStream(audioTrack);
      localAudioStreamRef.current = localAudioStream;

      logger.info('📻 Created LocalStageStream for audio');

      // Define strategy for publishing
      const strategy = {
        stageStreamsToPublish() {
          return [localAudioStream];
        },
        shouldPublishParticipant() {
          return true;
        },
        shouldSubscribeToParticipant() {
          return SubscribeType.NONE; // Broadcaster doesn't need to subscribe
        }
      };

      // Create and join Stage
      logger.info('🎭 Creating IVS Stage...');
      const stage = new Stage(tokenResponse.token, strategy);

      // Set up comprehensive stage event listeners with error handling
      setupStageEventListeners(stage);

      stageRef.current = stage;

      // Join the stage
      logger.info('📡 Joining IVS Stage...');
      await stage.join();

      logger.info('✅ IVS Stage connection established - NOW LIVE!');
      setStatus('live');

      // Register broadcast with global context
      startBroadcast(
        { id: streamId, name: streamName, streamingMode: 'browser' },
        stage,
        mediaStreamRef.current!
      );

      // Start duration timer
      setDuration(0);
      durationIntervalRef.current = window.setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

    } catch (err: any) {
      logger.error('Error starting broadcast', err);
      setError(err.message || 'Failed to start broadcasting');
      setStatus('error');

      // Clean up media stream and stop monitoring on error
      if (mediaStreamRef.current) {
        logger.info('🧹 Cleaning up media stream after error...');
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
      if (stageRef.current) {
        stageRef.current.leave();
        stageRef.current = null;
      }
      localAudioStreamRef.current = null;
      stopAudioLevelMonitoring();
    }
  };

  const stopBroadcasting = () => {
    logger.info('🛑 Stopping broadcast...');

    // Stop the global poller
    streamStatusPoller.stopPolling();

    // Stop duration timer
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // Leave IVS Stage
    if (stageRef.current) {
      logger.info('🚪 Leaving IVS Stage...');
      stageRef.current.leave();
      stageRef.current = null;
    }
    localAudioStreamRef.current = null;

    // Stop all tracks
    if (mediaStreamRef.current) {
      logger.info('🔇 Stopping media stream tracks...');
      mediaStreamRef.current.getTracks().forEach(track => {
        logger.info(`  Stopping track: ${track.label}`);
        track.stop();
      });
      mediaStreamRef.current = null;
    }

    stopAudioLevelMonitoring();
    setDuration(0);
    setStatus('idle');

    // Clear global broadcast context
    stopGlobalBroadcast();

    logger.info('✅ Broadcast stopped successfully');
  };

  const getStatusColor = () => {
    switch (status) {
      case 'live': return '#10b981';
      case 'connecting': return '#f59e0b';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = () => {
    if (isReconnecting) {
      return `Reconnecting... (${reconnectAttempts}/3)`;
    }
    switch (status) {
      case 'idle': return 'Ready to broadcast';
      case 'connecting': return 'Connecting...';
      case 'live': return 'LIVE';
      case 'stopped': return 'Broadcast stopped';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  return (
    <div className="browser-broadcaster">
      <div className="broadcaster-header">
        <h3>Browser Broadcasting (IVS Real-Time)</h3>
        <div className="status-indicator" style={{ backgroundColor: getStatusColor() }}>
          {status === 'live' && <span className="pulse-dot"></span>}
          {getStatusText()}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="error-close">&times;</button>
        </div>
      )}

      {isReconnecting && (
        <div className="reconnecting-message" style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '4px',
          padding: '12px',
          marginBottom: '15px',
          color: '#92400e'
        }}>
          🔄 Connection lost. Attempting to reconnect... ({reconnectAttempts}/3)
        </div>
      )}

      <div className="broadcaster-content">
        {/* Audio Device Selector */}
        <div className="device-selector">
          <label htmlFor="audioDevice">🎤 Audio Source</label>
          <select
            id="audioDevice"
            value={selectedDeviceId}
            onChange={(e) => setSelectedDeviceId(e.target.value)}
            disabled={status === 'live' || status === 'connecting'}
          >
            {devices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Device ${device.deviceId.slice(0, 8)}`}
              </option>
            ))}
          </select>
          <p className="device-hint">
            💡 Connect your DJ mixer, audio interface, or use virtual audio cable
          </p>
        </div>

        {/* Audio Level Meter */}
        <div className="audio-level-meter">
          <label>Audio Level</label>
          <div className="meter-container">
            <div
              className="meter-bar"
              style={{
                width: `${audioLevel}%`,
                backgroundColor: audioLevel > 80 ? '#ef4444' : audioLevel > 50 ? '#10b981' : '#22d3ee'
              }}
            />
          </div>
          <div className="meter-labels">
            <span>Quiet</span>
            <span>Good</span>
            <span>Loud</span>
          </div>
        </div>

        {/* Broadcasting Stats */}
        {status === 'live' && (
          <div className="broadcast-stats">
            <div className="stat">
              <span className="stat-label">Status:</span>
              <span className="stat-value">Broadcasting via IVS Stage</span>
            </div>
            <div className="stat">
              <span className="stat-label">Duration:</span>
              <span className="stat-value">{formatDuration(duration)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Protocol:</span>
              <span className="stat-value">IVS WebRTC</span>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="broadcaster-controls">
          {status === 'idle' || status === 'stopped' || status === 'error' ? (
            <>
              <button
                className="btn btn-primary btn-go-live"
                onClick={startBroadcasting}
                disabled={devices.length === 0 || isTesting}
                title={devices.length === 0 ? 'Please allow microphone access first' : isTesting ? 'Stop audio test first' : 'Start broadcasting'}
              >
                🎙️ Go Live
              </button>
              {!isTesting ? (
                <button
                  className="btn btn-secondary"
                  onClick={startTestAudio}
                  disabled={devices.length === 0}
                  style={{ marginLeft: '10px' }}
                  title="Test audio levels without broadcasting"
                >
                  🧪 Test Audio
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  onClick={stopTestAudio}
                  style={{ marginLeft: '10px' }}
                >
                  ⏹️ Stop Test
                </button>
              )}
              {error && (
                <button
                  className="btn btn-secondary"
                  onClick={loadAudioDevices}
                  style={{ marginLeft: '10px' }}
                >
                  🔄 Retry Permission
                </button>
              )}
            </>
          ) : (
            <button
              className="btn btn-danger"
              onClick={stopBroadcasting}
              disabled={status === 'connecting'}
            >
              ⏹️ Stop Broadcasting
            </button>
          )}
        </div>

        {status === 'idle' && devices.length === 0 && (
          <div className="setup-instructions">
            <h4>⚠️ Microphone Access Required</h4>
            <p>Please allow microphone access when prompted by your browser to continue.</p>
            {error && (
              <p style={{ color: '#ef4444', marginTop: '10px' }}>
                <strong>Error:</strong> {error}
              </p>
            )}
            <h4 style={{ marginTop: '20px' }}>Setup Instructions:</h4>
            <ol>
              <li>Click "Allow" when your browser asks for microphone permission</li>
              <li>If you blocked it, click the 🔄 Retry Permission button above</li>
              <li>Select your audio source from the dropdown (mixer, interface, or mic)</li>
              <li>Check the audio level meter shows activity</li>
              <li>Click "🎙️ Go Live" to start broadcasting via WebRTC</li>
            </ol>
            <p style={{ marginTop: '20px', fontSize: '14px', color: '#6b7280' }}>
              💡 <strong>Powered by Amazon IVS Real-Time Streaming</strong> - Ultra-low latency WebRTC broadcasting
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowserBroadcaster;
